import { useState } from 'react';
import { db } from '@/lib/db';
import type { Member } from '@/lib/db/types';
import { recordHandicapChange } from '@/lib/handicap-history';
import { Sheet } from '@/components/shared/Sheet';
import { Crest } from '@/components/shared/Crest';

interface Props {
  member: Member;
  onClose: () => void;
  onSaved: (m: Member) => void;
}

export function GhinConnectSheet({ member, onClose, onSaved }: Props) {
  const [ghinNum, setGhinNum] = useState(member.ghin_number || '');
  const [index, setIndex] = useState(member.handicap != null ? String(member.handicap) : '');
  const [saving, setSaving] = useState(false);

  const parsed = parseFloat(index);
  const valid = index !== '' && !Number.isNaN(parsed) && parsed >= -10 && parsed <= 54;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      if (member.handicap != null && parsed !== member.handicap) {
        recordHandicapChange(member.id, member.handicap, parsed);
      } else if (member.handicap == null) {
        recordHandicapChange(member.id, 0, parsed);
      }
      onSaved(await db.members.update({
        ghin_number: ghinNum.trim() || null,
        ghin_connected: false,
        handicap: parsed,
      }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title="Your Handicap" onClose={onClose}>
      <div className="px-5 pb-6 pt-2">
        <div className="relative mb-4 overflow-hidden rounded-xl bg-navy p-[18px] text-center text-cream">
          <div className="pointer-events-none absolute -right-3.5 -top-3.5 opacity-[0.08]">
            <Crest size={90} variant="dark" />
          </div>
          <div className="relative">
            <div className="mb-2.5 inline-block rounded-full bg-gold px-[11px] py-[5px] text-[10px] font-extrabold tracking-[0.12em] text-navy">
              SELF-REPORTED
            </div>
            <div className="font-serif text-xl font-bold tracking-[-0.01em]">Enter Your Current Index</div>
            <div className="mt-2 text-[11.5px] leading-[1.5] text-white/70">
              Look up your current handicap index on the official GHIN app or website, then enter it here. You can update it any time GHIN revises your number.
            </div>
          </div>
        </div>

        <div className="mb-4 rounded-md border-l-2 border-gold bg-gold/10 px-3 py-2.5 text-[10.5px] leading-[1.5] text-ink-soft">
          <strong className="text-navy">Why manual?</strong>{' '}
          Bag Tag doesn&apos;t pull data directly from the USGA Handicap System. Members at your club know each other — social trust + your own GHIN account keep the numbers honest. We&apos;ll add direct GHIN integration when our partnership with the USGA is in place.
        </div>

        <div className="mb-3.5">
          <div className="mb-1.5 text-xs font-bold tracking-wide text-navy">HANDICAP INDEX</div>
          <input
            type="number"
            step="0.1"
            value={index}
            onChange={(e) => setIndex(e.target.value)}
            placeholder="e.g. 11.5"
            className="w-full rounded-[10px] border border-line bg-cream px-4 py-3.5 text-center text-2xl font-bold text-navy outline-none"
          />
          <div className="mt-1.5 text-[10.5px] leading-snug text-mute">Your USGA Handicap Index. Range: -10.0 to 54.0.</div>
        </div>

        <div className="mb-[18px]">
          <div className="mb-1.5 text-xs font-bold tracking-wide text-navy">GHIN NUMBER (OPTIONAL)</div>
          <input
            type="text"
            inputMode="numeric"
            value={ghinNum}
            onChange={(e) => setGhinNum(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 4421890"
            className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-base text-ink outline-none"
          />
          <div className="mt-1.5 text-[10.5px] leading-snug text-mute">
            Stored as identity only. Lets other members cross-reference your number on the official GHIN site if needed.
          </div>
        </div>

        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[10px] border border-line bg-cream px-3.5 py-3.5 text-[13px] font-semibold text-ink-soft"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!valid || saving}
            className={`flex-[2] rounded-[10px] px-3.5 py-3.5 text-[13px] font-bold tracking-wide ${
              valid ? 'bg-navy text-paper' : 'bg-line text-mute opacity-60'
            } disabled:opacity-60`}
          >
            {saving ? 'Saving…' : 'Save Handicap'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
